import React, { useCallback, useEffect, useState } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Gift,
  Clock,
  Pause,
  Play,
  Ban,
  RotateCcw,
  Ticket as TicketIcon,
  CalendarPlus,
  History,
  Package,
  SlidersHorizontal,
  Receipt,
  Loader2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import useMoedaAtiva, {
  simboloMoeda,
  formatarMoeda,
  moedaPlataforma,
} from "../../hooks/useMoedaAtiva";

// A plataforma nem sempre cobra em real: pelo Stripe a moeda é a que o
// operador escolheu. Ver "R$" numa cobrança em euro seria mentira.
const brl = (v) => formatarMoeda(v, moedaPlataforma());

const SITUACAO = {
  active: { rotulo: "Ativa", classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  trial: { rotulo: "Em teste", classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
  past_due: { rotulo: "Em atraso", classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  paused: { rotulo: "Pausada", classe: "bg-slate-500/10 text-slate-600 dark:text-slate-400" },
  canceled: { rotulo: "Cancelada", classe: "bg-destructive/10 text-destructive" },
};

const ORIGEM = {
  plan: { rotulo: "Incluso no plano", classe: "bg-primary/10 text-primary" },
  extra: { rotulo: "Contratado à parte", classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  free: { rotulo: "Cortesia", classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
  trial: { rotulo: "Em teste", classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
};

const RECURSOS = [
  { key: "users", label: "Usuários" },
  { key: "connections", label: "Conexões" },
  { key: "queues", label: "Filas" },
];

// Cartão de seção: título curto + uma frase que explica para que serve.
const Secao = ({ icone: Icone, titulo, ajuda, acao, children }) => (
  <section className="rounded-xl border bg-card">
    <header className="flex flex-wrap items-start justify-between gap-3 border-b p-4">
      <div className="flex min-w-0 items-start gap-3">
        {Icone && (
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Icone className="h-4 w-4 text-muted-foreground" />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{titulo}</h2>
          {ajuda && (
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              {ajuda}
            </p>
          )}
        </div>
      </div>
      {acao}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

const CompanySubscription = () => {
  // Puxa a moeda da plataforma; os formatadores acima leem dela.
  useMoedaAtiva();

  const { companyId } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [aba, setAba] = useState("resumo");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const carregar = useCallback(async () => {
    try {
      const [assinatura, planos, modulos] = await Promise.all([
        api.get(`/companies/${companyId}/subscription`),
        api.get("/plans"),
        api.get("/modules?onlyActive=true"),
      ]);
      setDados(assinatura.data);
      setPlans(planos.data);
      setModules(modulos.data);
    } catch (err) {
      toastError(err);
    }
    setCarregando(false);
  }, [companyId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const chamar = async (metodo, rota, corpo, mensagem) => {
    setSalvando(true);
    try {
      await api[metodo](rota, corpo);
      if (mensagem) toast.success(mensagem);
      await carregar();
      setModal(null);
      setForm({});
    } catch (err) {
      toastError(err);
    }
    setSalvando(false);
  };

  const abrir = (nome, inicial = {}) => {
    const padrao = {
      moduleIds: [],
      origin: "extra",
      customPrice: "",
      trialDays: 7,
      reason: "",
    };
    if (nome === "modulo") {
      setForm({ ...padrao, ...inicial });
    } else {
      setForm(inicial);
    }
    setModal(nome);
  };

  if (carregando) {
    return (
      <MainContainer>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MainContainer>
    );
  }

  const { subscription: assinatura, resumo, overrides } = dados || {};
  const situacao = SITUACAO[assinatura?.status] || SITUACAO.active;

  const modulosDisponiveis = modules.filter(
    (m) => !resumo?.modules?.some((r) => r.key === m.key)
  );

  return (
    <MainContainer>
      {/* ---------------- cabeçalho ---------------- */}
      <div className="flex flex-col gap-3 border-b px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label="Voltar para empresas"
            onClick={() => navigate("/companies")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold tracking-tight">
              Assinatura
            </h1>
            <p className="text-xs text-muted-foreground">
              Plano, módulos e valor desta empresa
            </p>
          </div>
          <span
            className={cn(
              "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium",
              situacao.classe
            )}
          >
            {situacao.rotulo}
          </span>
        </div>
      </div>

      <PageTabs
        abas={[
          { value: "resumo", rotulo: "Resumo", icone: Receipt },
          { value: "plano", rotulo: "Plano", icone: Package },
          {
            value: "modulos",
            rotulo: "Módulos",
            icone: Gift,
            contador: resumo?.modules?.length || 0,
          },
          {
            value: "limites",
            rotulo: "Limites",
            icone: SlidersHorizontal,
            contador: overrides?.length || 0,
          },
          { value: "desconto", rotulo: "Desconto", icone: TicketIcon },
          { value: "situacao", rotulo: "Situação", icone: Clock },
        ]}
        atual={aba}
        onChange={setAba}
      />

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {/* ---------------- resumo ---------------- */}
        {aba === "resumo" && (
          <section className="overflow-hidden rounded-xl border bg-card">
            <div className="border-b bg-muted/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Valor da mensalidade
              </p>
              <p className="mt-1 text-3xl font-semibold tabular-nums">
                {brl(resumo?.total)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {resumo?.planName || "Sem plano"} ·{" "}
                {assinatura?.billingCycle === "yearly"
                  ? "cobrado por ano"
                  : assinatura?.billingCycle === "quarterly"
                  ? "cobrado a cada 3 meses"
                  : "cobrado por mês"}
              </p>
            </div>

            {/* Cada linha mostra de onde vem o valor. */}
            <ul className="divide-y">
              {(resumo?.linhas || []).map((linha, indice) => (
                <li
                  key={indice}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                >
                  <span className="min-w-0 truncate">{linha.descricao}</span>
                  <span
                    className={cn(
                      "shrink-0 tabular-nums",
                      linha.valor < 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-foreground"
                    )}
                  >
                    {linha.valor < 0 ? "−" : ""}
                    {brl(Math.abs(linha.valor))}
                  </span>
                </li>
              ))}
              {(resumo?.linhas || []).length === 0 && (
                <li className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nada cobrado ainda. Escolha um plano na aba Plano.
                </li>
              )}
            </ul>

            <div className="border-t bg-muted/20 p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Limites contratados
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {RECURSOS.map((recurso) => (
                  <div key={recurso.key} className="rounded-lg bg-card p-3">
                    <p className="text-xs text-muted-foreground">
                      {recurso.label}
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums">
                      {resumo?.limits?.[recurso.key] > 0
                        ? resumo.limits[recurso.key]
                        : "Ilimitado"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ---------------- plano ---------------- */}
        {aba === "plano" && (
          <Secao
            icone={Package}
            titulo="Plano contratado"
            ajuda="Trocar o plano mantém todos os dados da empresa — muda só o que ela pode usar."
            acao={
              <Button
                size="sm"
                className="h-8"
                onClick={() =>
                  abrir("plano", {
                    planId: assinatura?.planId ? String(assinatura.planId) : "",
                    customPlanPrice: assinatura?.customPlanPrice ?? "",
                    billingCycle: assinatura?.billingCycle || "monthly",
                  })
                }
              >
                Alterar plano
              </Button>
            }
          >
            <div className="space-y-3">
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium">
                  {resumo?.planName || "Nenhum plano selecionado"}
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {brl(resumo?.planPrice)}
                </p>
                {assinatura?.customPlanPrice !== null &&
                  assinatura?.customPlanPrice !== undefined && (
                    <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
                      Valor exclusivo desta empresa
                    </p>
                  )}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {RECURSOS.map((recurso) => (
                  <div key={recurso.key} className="rounded-lg bg-muted/40 p-3">
                    <p className="text-xs text-muted-foreground">
                      {recurso.label}
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums">
                      {resumo?.limits?.[recurso.key] > 0
                        ? resumo.limits[recurso.key]
                        : "Ilimitado"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Secao>
        )}

        {/* ---------------- módulos ---------------- */}
        {aba === "modulos" && (
          <Secao
            icone={Gift}
            titulo="Módulos ativos"
            ajuda="Adicione um recurso avulso sem trocar o plano. Remover faz o valor voltar ao que era."
            acao={
              <Button
                size="sm"
                className="h-8"
                disabled={modulosDisponiveis.length === 0}
                onClick={() =>
                  abrir("modulo", {
                    origin: "extra",
                    moduleId: "",
                    trialDays: 7,
                  })
                }
              >
                <Plus className="h-4 w-4" />
                Adicionar
              </Button>
            }
          >
            {(resumo?.modules || []).length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhum módulo ativo.
              </p>
            ) : (
              <ul className="space-y-2">
                {resumo.modules.map((modulo) => {
                  const origem = ORIGEM[modulo.origin] || ORIGEM.extra;
                  // Módulo liberado por padrão não sai por aqui: apagar o
                  // vínculo o traria de volta. Quem desliga é o botão da
                  // listagem de empresas.
                  const naoRemovivel =
                    modulo.isRequired ||
                    modulo.origin === "plan" ||
                    modulo.ligadoPorPadrao;

                  return (
                    <li
                      key={modulo.key}
                      className="flex flex-wrap items-center gap-2 rounded-lg border p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {modulo.name}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-[10px] font-medium",
                              origem.classe
                            )}
                          >
                            {origem.rotulo}
                          </span>
                          {modulo.isRequired && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                              Obrigatório
                            </span>
                          )}
                          {modulo.ligadoPorPadrao && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                              Padrão
                            </span>
                          )}
                          {modulo.expiresAt && (
                            <span className="text-[10px] text-muted-foreground">
                              até{" "}
                              {new Date(modulo.expiresAt).toLocaleDateString(
                                "pt-BR"
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                        {modulo.price > 0 ? brl(modulo.price) : "—"}
                      </span>

                      {!naoRemovivel && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          aria-label={"Remover " + modulo.name}
                          onClick={() =>
                            abrir("removerModulo", {
                              key: modulo.key,
                              name: modulo.name,
                              moduleId: modules.find(
                                (m) => m.key === modulo.key
                              )?.id,
                            })
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Secao>
        )}

        {/* ---------------- limites ---------------- */}
        {aba === "limites" && (
          <Secao
            icone={SlidersHorizontal}
            titulo="Limites extras"
            ajuda="Aumente só um recurso — mais usuários, por exemplo — sem obrigar a troca de plano."
            acao={
              <Button
                size="sm"
                className="h-8"
                onClick={() =>
                  abrir("limite", {
                    resource: "users",
                    extraAmount: 1,
                    price: 0,
                  })
                }
              >
                <Plus className="h-4 w-4" />
                Adicionar
              </Button>
            }
          >
            {(overrides || []).length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhum limite extra contratado.
              </p>
            ) : (
              <ul className="space-y-2">
                {overrides.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center gap-2 rounded-lg border p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">
                        +{item.extraAmount}{" "}
                        {RECURSOS.find((r) => r.key === item.resource)?.label ||
                          item.resource}
                      </p>
                      {item.expiresAt && (
                        <p className="text-[10px] text-muted-foreground">
                          até{" "}
                          {new Date(item.expiresAt).toLocaleDateString("pt-BR")}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                      {brl(item.price)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label="Remover limite extra"
                      onClick={() =>
                        chamar(
                          "delete",
                          "/companies/" + companyId + "/limits/" + item.id,
                          undefined,
                          "Limite removido."
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Secao>
        )}

        {/* ---------------- desconto ---------------- */}
        {aba === "desconto" && (
          <Secao
            icone={TicketIcon}
            titulo="Desconto e acréscimo"
            ajuda="Vale só para esta empresa. O cupom soma ao desconto manual."
            acao={
              <Button
                size="sm"
                className="h-8"
                onClick={() =>
                  abrir("desconto", {
                    discountPercent: assinatura?.discountPercent || 0,
                    discountValue: assinatura?.discountValue || 0,
                    surchargeValue: assinatura?.surchargeValue || 0,
                    discountUntil: assinatura?.discountUntil
                      ? String(assinatura.discountUntil).slice(0, 10)
                      : "",
                  })
                }
              >
                Ajustar
              </Button>
            }
          >
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">Desconto</p>
                  <p className="mt-0.5 text-lg font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                    {brl(resumo?.discount)}
                  </p>
                </div>
                <div className="rounded-lg bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">Acréscimo</p>
                  <p className="mt-0.5 text-lg font-semibold tabular-nums">
                    {brl(resumo?.surcharge)}
                  </p>
                </div>
                <div className="rounded-lg bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">Vale até</p>
                  <p className="mt-0.5 text-sm font-medium">
                    {assinatura?.discountUntil
                      ? new Date(assinatura.discountUntil).toLocaleDateString(
                          "pt-BR"
                        )
                      : "Permanente"}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 border-t pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => abrir("cupom", { code: "" })}
                >
                  <TicketIcon className="h-4 w-4" />
                  Aplicar cupom
                </Button>
                {assinatura?.couponId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() =>
                      chamar(
                        "delete",
                        "/companies/" + companyId + "/subscription/coupon",
                        undefined,
                        "Cupom removido."
                      )
                    }
                  >
                    Remover cupom
                  </Button>
                )}
              </div>
            </div>
          </Secao>
        )}

        {/* ---------------- situação ---------------- */}
        {aba === "situacao" && (
          <Secao
            icone={Clock}
            titulo="Situação da assinatura"
            ajuda="Pausar ou cancelar não apaga nada: os dados continuam guardados."
          >
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-muted/40 p-3">
                <p className="text-xs text-muted-foreground">Vence em</p>
                <p className="mt-0.5 text-sm font-medium tabular-nums">
                  {assinatura?.currentPeriodEnd
                    ? new Date(assinatura.currentPeriodEnd).toLocaleDateString(
                        "pt-BR"
                      )
                    : "Não definido"}
                </p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3">
                <p className="text-xs text-muted-foreground">
                  Dias de cortesia
                </p>
                <p className="mt-0.5 text-sm font-medium tabular-nums">
                  {assinatura?.graceDays || 0}
                </p>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => abrir("trial", { days: 14, moduleIds: [] })}
              >
                <Clock className="h-4 w-4" />
                Liberar período de teste
              </Button>

              <Button
                variant="outline"
                className="justify-start"
                onClick={() => abrir("cortesia", { days: 5 })}
              >
                <CalendarPlus className="h-4 w-4" />
                Dar dias de cortesia
              </Button>

              {assinatura?.status === "paused" ? (
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() =>
                    chamar(
                      "post",
                      "/companies/" + companyId + "/subscription/resume",
                      { reason: "Retomada pelo painel" },
                      "Assinatura retomada."
                    )
                  }
                >
                  <Play className="h-4 w-4" />
                  Retomar assinatura
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => abrir("pausar", { until: "" })}
                >
                  <Pause className="h-4 w-4" />
                  Pausar assinatura
                </Button>
              )}

              {assinatura?.status === "canceled" ? (
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() =>
                    chamar(
                      "post",
                      "/companies/" + companyId + "/subscription/restore",
                      { reason: "Restaurada pelo painel" },
                      "Assinatura restaurada."
                    )
                  }
                >
                  <RotateCcw className="h-4 w-4" />
                  Restaurar assinatura
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="justify-start text-destructive hover:text-destructive"
                  onClick={() => abrir("cancelar", { immediate: false })}
                >
                  <Ban className="h-4 w-4" />
                  Cancelar assinatura
                </Button>
              )}
            </div>
          </Secao>
        )}
      </div>

      {/* ================= modais ================= */}

      {/* plano */}
      <Dialog open={modal === "plano"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar plano</DialogTitle>
            <DialogDescription>
              Todos os dados da empresa são mantidos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Plano</Label>
              <Select
                value={form.planId}
                onValueChange={(v) => setForm({ ...form, planId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name} — {brl(p.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="customPlanPrice">
                Valor exclusivo desta empresa
              </Label>
              <Input
                id="customPlanPrice"
                type="number"
                step="0.01"
                min="0"
                placeholder="Deixe vazio para usar o valor do plano"
                value={form.customPlanPrice}
                onChange={(e) =>
                  setForm({ ...form, customPlanPrice: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                Não altera o plano para as outras empresas.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Cobrança</Label>
              <Select
                value={form.billingCycle}
                onValueChange={(v) => setForm({ ...form, billingCycle: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensal</SelectItem>
                  <SelectItem value="quarterly">Trimestral</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="motivoPlano">Motivo</Label>
              <Input
                id="motivoPlano"
                placeholder="Fica registrado no histórico"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={salvando}
              onClick={() =>
                chamar(
                  "put",
                  `/companies/${companyId}/subscription`,
                  {
                    planId: form.planId ? Number(form.planId) : null,
                    customPlanPrice:
                      form.customPlanPrice === ""
                        ? null
                        : Number(form.customPlanPrice),
                    billingCycle: form.billingCycle,
                    reason: form.reason,
                  },
                  "Plano alterado."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* módulo */}
      <Dialog open={modal === "modulo"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-lg p-4 sm:p-5">
          <DialogHeader className="space-y-1">
            <DialogTitle>Adicionar módulo</DialogTitle>
            <DialogDescription>
              Libera um recurso sem trocar o plano.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <Label>Módulos disponíveis</Label>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <button
                    type="button"
                    disabled={modulosDisponiveis.length === 0}
                    onClick={() =>
                      setForm({
                        ...form,
                        moduleIds: modulosDisponiveis.map((m) => String(m.id)),
                      })
                    }
                    className="font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Selecionar todos
                  </button>
                  <button
                    type="button"
                    disabled={!form.moduleIds || form.moduleIds.length === 0}
                    onClick={() => setForm({ ...form, moduleIds: [] })}
                    className="font-medium text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Limpar
                  </button>
                  <span>
                    {Array.isArray(form.moduleIds) ? form.moduleIds.length : 0} de{" "}
                    {modulosDisponiveis.length}
                  </span>
                </div>
              </div>

              {modulosDisponiveis.length === 0 ? (
                <div className="rounded-lg border border-dashed p-3 text-center text-sm text-muted-foreground">
                  Todos os módulos já foram adicionados a esta empresa.
                </div>
              ) : (
                <div className="max-h-44 overflow-y-auto rounded-lg border bg-card">
                  {modulosDisponiveis.map((m) => {
                    const idStr = String(m.id);
                    const marcado = Array.isArray(form.moduleIds)
                      ? form.moduleIds.includes(idStr)
                      : false;
                    return (
                      <label
                        key={m.id}
                        className="flex items-center gap-3 border-b px-3 py-1.5 last:border-b-0 hover:bg-muted/40 cursor-pointer"
                      >
                        <Checkbox
                          checked={marcado}
                          onCheckedChange={(valor) => {
                            const antes = Array.isArray(form.moduleIds)
                              ? form.moduleIds
                              : [];
                            const proximos =
                              valor === true || valor === "indeterminate"
                                ? [...antes, idStr]
                                : antes.filter((x) => x !== idStr);
                            setForm({ ...form, moduleIds: proximos });
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">
                              {m.name}
                            </span>
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {Number(m.price) > 0 ? brl(m.price) : "Grátis"}
                            </span>
                          </div>
                          {m.description ? (
                            <p className="text-xs text-muted-foreground whitespace-normal break-words leading-snug line-clamp-3">
                              {m.description}
                            </p>
                          ) : null}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Como será cobrado</Label>
              <Select
                value={form.origin}
                onValueChange={(v) => setForm({ ...form, origin: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="extra">Cobrar na mensalidade</SelectItem>
                  <SelectItem value="free">Cortesia (não cobra)</SelectItem>
                  <SelectItem value="trial">Teste por alguns dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.origin === "extra" && (
              <div className="space-y-1.5">
                <Label htmlFor="customPrice">
                  Preço para esta empresa (por módulo)
                </Label>
                <Input
                  id="customPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Deixe vazio para usar o preço do módulo"
                  value={form.customPrice || ""}
                  onChange={(e) =>
                    setForm({ ...form, customPrice: e.target.value })
                  }
                />
              </div>
            )}

            {form.origin === "trial" && (
              <div className="space-y-1.5">
                <Label htmlFor="trialDays">Dias de teste</Label>
                <Input
                  id="trialDays"
                  type="number"
                  min="1"
                  value={form.trialDays ?? 7}
                  onChange={(e) =>
                    setForm({ ...form, trialDays: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Os módulos saem sozinhos quando o prazo acabar.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="motivoModulo">Motivo</Label>
              <Input
                id="motivoModulo"
                placeholder="Fica registrado no histórico"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter className="pt-1">
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={
                salvando ||
                !Array.isArray(form.moduleIds) ||
                form.moduleIds.length === 0
              }
              onClick={() =>
                chamar(
                  "post",
                  `/companies/${companyId}/modules`,
                  {
                    moduleIds: form.moduleIds.map((id) => Number(id)),
                    origin: form.origin,
                    customPrice: form.customPrice
                      ? Number(form.customPrice)
                      : null,
                    trialDays: Number(form.trialDays) || undefined,
                    reason: form.reason,
                  },
                  form.moduleIds.length === 1
                    ? "Módulo adicionado."
                    : `${form.moduleIds.length} módulos adicionados.`
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* remover módulo */}
      <Dialog
        open={modal === "removerModulo"}
        onOpenChange={() => setModal(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remover {form.name}?</DialogTitle>
            <DialogDescription>
              A empresa perde o acesso e o valor volta ao que era antes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="motivoRemover">Motivo</Label>
            <Input
              id="motivoRemover"
              placeholder="Ex.: cliente cancelou"
              value={form.reason || ""}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={salvando}
              onClick={() =>
                chamar(
                  "delete",
                  `/companies/${companyId}/modules/${form.moduleId}`,
                  undefined,
                  "Módulo removido."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* limite extra */}
      <Dialog open={modal === "limite"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Limite extra</DialogTitle>
            <DialogDescription>
              Soma ao limite do plano, só para esta empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Recurso</Label>
              <Select
                value={form.resource}
                onValueChange={(v) => setForm({ ...form, resource: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECURSOS.map((r) => (
                    <SelectItem key={r.key} value={r.key}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="extraAmount">Quantidade</Label>
                <Input
                  id="extraAmount"
                  type="number"
                  min="1"
                  value={form.extraAmount}
                  onChange={(e) =>
                    setForm({ ...form, extraAmount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="precoLimite">Valor ({moedaPlataforma()})</Label>
                <Input
                  id="precoLimite"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motivoLimite">Motivo</Label>
              <Input
                id="motivoLimite"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={salvando}
              onClick={() =>
                chamar(
                  "post",
                  `/companies/${companyId}/limits`,
                  {
                    resource: form.resource,
                    extraAmount: Number(form.extraAmount),
                    price: Number(form.price) || 0,
                    reason: form.reason,
                  },
                  "Limite adicionado."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* desconto */}
      <Dialog open={modal === "desconto"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Desconto e acréscimo</DialogTitle>
            <DialogDescription>
              Aplicado só na mensalidade desta empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="discountPercent">Desconto (%)</Label>
                <Input
                  id="discountPercent"
                  type="number"
                  min="0"
                  max="100"
                  value={form.discountPercent}
                  onChange={(e) =>
                    setForm({ ...form, discountPercent: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="discountValue">
                  Desconto ({simboloMoeda(moedaPlataforma())})
                </Label>
                <Input
                  id="discountValue"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.discountValue}
                  onChange={(e) =>
                    setForm({ ...form, discountValue: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discountUntil">Desconto vale até</Label>
              <Input
                id="discountUntil"
                type="date"
                value={form.discountUntil}
                onChange={(e) =>
                  setForm({ ...form, discountUntil: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                Vazio = desconto permanente.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="surchargeValue">
                Acréscimo ({simboloMoeda(moedaPlataforma())})
              </Label>
              <Input
                id="surchargeValue"
                type="number"
                step="0.01"
                min="0"
                value={form.surchargeValue}
                onChange={(e) =>
                  setForm({ ...form, surchargeValue: e.target.value })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motivoDesconto">Motivo</Label>
              <Input
                id="motivoDesconto"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={salvando}
              onClick={() =>
                chamar(
                  "put",
                  `/companies/${companyId}/subscription`,
                  {
                    discountPercent: Number(form.discountPercent) || 0,
                    discountValue: Number(form.discountValue) || 0,
                    surchargeValue: Number(form.surchargeValue) || 0,
                    discountUntil: form.discountUntil || null,
                    reason: form.reason,
                  },
                  "Desconto atualizado."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* cupom */}
      <Dialog open={modal === "cupom"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Aplicar cupom</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="codigoCupom">Código</Label>
            <Input
              id="codigoCupom"
              placeholder="BEMVINDO20"
              className="uppercase"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={salvando || !form.code}
              onClick={() =>
                chamar(
                  "post",
                  `/companies/${companyId}/subscription/coupon`,
                  { code: form.code, reason: "Cupom aplicado pelo painel" },
                  "Cupom aplicado."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* trial */}
      <Dialog open={modal === "trial"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Liberar teste</DialogTitle>
            <DialogDescription>
              Os módulos escolhidos saem sozinhos quando o prazo acabar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="diasTeste">Dias de teste</Label>
              <Input
                id="diasTeste"
                type="number"
                min="1"
                value={form.days}
                onChange={(e) => setForm({ ...form, days: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Módulos liberados no teste</Label>
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border p-2">
                {modulosDisponiveis.map((m) => {
                  const marcado = (form.moduleIds || []).includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          moduleIds: marcado
                            ? form.moduleIds.filter((id) => id !== m.id)
                            : [...(form.moduleIds || []), m.id],
                        })
                      }
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                        marcado ? "bg-primary/10 text-primary" : "hover:bg-muted"
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                          marcado && "border-primary bg-primary"
                        )}
                      />
                      <span className="truncate">{m.name}</span>
                    </button>
                  );
                })}
                {modulosDisponiveis.length === 0 && (
                  <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                    Todos os módulos já estão ativos.
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={salvando}
              onClick={() =>
                chamar(
                  "post",
                  `/companies/${companyId}/subscription/trial`,
                  {
                    days: Number(form.days),
                    moduleIds: form.moduleIds || [],
                    reason: "Teste liberado pelo painel",
                  },
                  "Teste liberado."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Liberar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* cortesia */}
      <Dialog open={modal === "cortesia"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Dar dias de cortesia</DialogTitle>
            <DialogDescription>
              Adia o bloqueio sem mudar a data oficial de vencimento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="diasCortesia">Dias</Label>
              <Input
                id="diasCortesia"
                type="number"
                min="1"
                value={form.days}
                onChange={(e) => setForm({ ...form, days: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motivoCortesia">Motivo</Label>
              <Input
                id="motivoCortesia"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={salvando}
              onClick={() =>
                chamar(
                  "post",
                  `/companies/${companyId}/subscription/grace-days`,
                  { days: Number(form.days), reason: form.reason },
                  "Dias concedidos."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Conceder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* pausar */}
      <Dialog open={modal === "pausar"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Pausar assinatura</DialogTitle>
            <DialogDescription>
              O acesso fica suspenso, mas nada é apagado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="pausarAte">Pausar até</Label>
              <Input
                id="pausarAte"
                type="date"
                value={form.until}
                onChange={(e) => setForm({ ...form, until: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Vazio = pausa por tempo indeterminado.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motivoPausa">Motivo</Label>
              <Input
                id="motivoPausa"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              disabled={salvando}
              onClick={() =>
                chamar(
                  "post",
                  `/companies/${companyId}/subscription/pause`,
                  { until: form.until || null, reason: form.reason },
                  "Assinatura pausada."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Pausar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* cancelar */}
      <Dialog open={modal === "cancelar"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancelar assinatura</DialogTitle>
            <DialogDescription>
              Os dados continuam guardados e podem ser restaurados depois.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              {[
                {
                  valor: false,
                  titulo: "No fim do período pago",
                  ajuda: "A empresa usa até a data de vencimento.",
                },
                {
                  valor: true,
                  titulo: "Agora",
                  ajuda: "O acesso é bloqueado imediatamente.",
                },
              ].map((opcao) => (
                <button
                  key={String(opcao.valor)}
                  type="button"
                  onClick={() => setForm({ ...form, immediate: opcao.valor })}
                  className={cn(
                    "w-full rounded-lg border p-3 text-left transition-colors",
                    form.immediate === opcao.valor
                      ? "border-primary bg-primary/5"
                      : "hover:bg-muted"
                  )}
                >
                  <p className="text-sm font-medium">{opcao.titulo}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {opcao.ajuda}
                  </p>
                </button>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motivoCancel">Motivo</Label>
              <Input
                id="motivoCancel"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Voltar
            </Button>
            <Button
              variant="destructive"
              disabled={salvando}
              onClick={() =>
                chamar(
                  "post",
                  `/companies/${companyId}/subscription/cancel`,
                  { immediate: form.immediate, reason: form.reason },
                  "Cancelamento registrado."
                )
              }
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainContainer>
  );
};

export default CompanySubscription;
